/**
 * WeatherWidget.jsx — V1-style single-city weather with full detail
 *
 * City pill selector, large current display, drone-ops alerts,
 * 2×2 metrics grid, 7-day forecast strip.
 * Uses Open-Meteo free API (no key required).
 * Auto-refreshes every 30 minutes. Caches per city.
 */

import { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';

const LOCATIONS = [
  { name: 'Santa Barbara', lat: 34.4208, lon: -119.6982 },
  { name: 'Ventura', lat: 34.2644, lon: -119.2148 },
  { name: 'Santa Monica', lat: 34.0195, lon: -118.4912 },
  { name: 'Solvang', lat: 34.4830, lon: -120.1057 },
  { name: 'SLO', lat: 35.2828, lon: -120.6596 },
];

const WMO_CODES = {
  0: '☀️ Clear', 1: '🌤 Mostly Clear', 2: '⛅ Partly Cloudy', 3: '☁️ Overcast',
  45: '🌫 Fog', 48: '🌫 Rime Fog',
  51: '🌦 Drizzle', 53: '🌦 Drizzle', 55: '🌧 Heavy Drizzle',
  61: '🌧 Light Rain', 63: '🌧 Rain', 65: '🌧 Heavy Rain',
  71: '🌨 Light Snow', 73: '🌨 Snow', 75: '🌨 Heavy Snow',
  80: '🌧 Showers', 81: '🌧 Mod. Showers', 82: '🌧 Heavy Showers',
  95: '⛈ Thunderstorm', 96: '⛈ T-Storm + Hail', 99: '⛈ Heavy T-Storm',
};

function wxEmoji(c) { return (WMO_CODES[c] || '🌤 Fair').split(' ')[0]; }
function wxLabel(c) { return (WMO_CODES[c] || 'Fair').split(' ').slice(1).join(' '); }

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CACHE_KEY = 'rfos_wx_cache';
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

export default function WeatherWidget() {
  const [cityIdx, setCityIdx] = useState(0);
  const [current, setCurrent] = useState(null);
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const city = LOCATIONS[cityIdx];

  const fetchWeather = useCallback(async (loc) => {
    try {
      // Check cache
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.city === loc.name && (Date.now() - cached.ts) < CACHE_TTL) {
        setCurrent(cached.current);
        setDaily(cached.daily);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const url = `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,wind_gusts_10m,precipitation,visibility,cloud_cover&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,precipitation_sum&wind_speed_unit=mph&temperature_unit=fahrenheit&precipitation_unit=inch&timezone=auto&forecast_days=7`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      localStorage.setItem(CACHE_KEY, JSON.stringify({ current: data.current, daily: data.daily, city: loc.name, ts: Date.now() }));
      setCurrent(data.current);
      setDaily(data.daily);
    } catch (err) {
      console.error('Weather fetch failed:', err);
      setError('Weather unavailable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeather(city);
    const interval = setInterval(() => fetchWeather(city), CACHE_TTL);
    return () => clearInterval(interval);
  }, [city, fetchWeather]);

  function selectCity(i) {
    setCityIdx(i);
    localStorage.removeItem(CACHE_KEY);
  }

  // Drone-ops alerts
  const alerts = [];
  if (current) {
    const wind = current.wind_speed_10m;
    const gusts = current.wind_gusts_10m;
    if (wind > 20) alerts.push({ color: '#dc2626', msg: `⚠ Wind ${wind}mph — exceeds safe limit` });
    else if (wind > 15) alerts.push({ color: '#F59E0B', msg: `↗ Wind ${wind}mph — marginal` });
    if (gusts > 25) alerts.push({ color: '#dc2626', msg: `⚠ Gusts ${gusts}mph` });
    if (current.precipitation > 0) alerts.push({ color: '#dc2626', msg: '⚠ Precipitation' });
    if (current.visibility < 4828) alerts.push({ color: '#dc2626', msg: '⚠ Low visibility' });
    if (!alerts.length) alerts.push({ color: '#10B981', msg: '✓ Good for drone ops' });
  }

  const monoSx = { fontFamily: '"DM Mono", monospace' };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Weather</Typography>

      {/* City pills */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
        {LOCATIONS.map((loc, i) => (
          <Chip
            key={loc.name}
            label={loc.name}
            size="small"
            onClick={() => selectCity(i)}
            sx={{
              fontSize: '0.65rem', ...monoSx,
              bgcolor: i === cityIdx ? '#13B5EA' : 'transparent',
              color: i === cityIdx ? '#fff' : 'text.secondary',
              border: i === cityIdx ? 'none' : '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: i === cityIdx ? '#0A9FD4' : 'action.hover' },
            }}
          />
        ))}
      </Box>

      {loading && !current ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Loading weather...</Typography>
      ) : error && !current ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{error}</Typography>
      ) : current ? (
        <>
          {/* City label */}
          <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.7rem', mb: 0.5, display: 'block' }}>
            📍 {city.name}, CA
          </Typography>

          {/* Big current display */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography sx={{ fontSize: '2.2rem', lineHeight: 1 }}>{wxEmoji(current.weather_code)}</Typography>
            <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2.4rem', lineHeight: 1, color: '#13B5EA' }}>
              {Math.round(current.temperature_2m)}°F
            </Typography>
          </Box>

          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {wxLabel(current.weather_code)} · Feels {Math.round(current.apparent_temperature)}°F
          </Typography>

          {/* Drone alerts */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
            {alerts.map((a, i) => (
              <Typography key={i} variant="caption" sx={{ ...monoSx, fontSize: '0.7rem', color: a.color }}>{a.msg}</Typography>
            ))}
          </Box>

          {/* 2x2 metrics grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
            {[
              { label: 'Wind', value: `${current.wind_speed_10m} mph`, color: current.wind_speed_10m > 20 ? '#dc2626' : current.wind_speed_10m > 15 ? '#F59E0B' : 'text.primary' },
              { label: 'Gusts', value: `${current.wind_gusts_10m} mph` },
              { label: 'Cloud', value: `${current.cloud_cover}%` },
              { label: 'Visibility', value: `${(current.visibility / 1609.34).toFixed(1)} mi` },
            ].map((m) => (
              <Box key={m.label} sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.6rem', textTransform: 'uppercase' }}>{m.label}</Typography>
                <Typography variant="body2" sx={{ ...monoSx, fontWeight: 600, color: m.color || 'text.primary' }}>{m.value}</Typography>
              </Box>
            ))}
          </Box>

          {/* 7-day forecast */}
          {daily?.time && (
            <Box sx={{ display: 'flex', gap: '3px', overflowX: 'auto', pb: 0.5 }}>
              {daily.time.slice(1).map((dt, i) => {
                const idx = i + 1;
                const d = new Date(dt + 'T12:00');
                const wnd = Math.round(daily.wind_speed_10m_max[idx]);
                const rain = daily.precipitation_sum[idx];
                const windColor = wnd > 20 ? '#dc2626' : wnd > 15 ? '#F59E0B' : 'text.disabled';
                return (
                  <Box key={dt} sx={{ flex: '1 0 58px', textAlign: 'center', p: '6px 4px', bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Typography sx={{ ...monoSx, fontSize: '0.6rem', color: 'text.secondary', mb: '2px' }}>{DAY_NAMES[d.getDay()]}</Typography>
                    <Typography sx={{ fontSize: '1rem', mb: '2px' }}>{wxEmoji(daily.weather_code[idx])}</Typography>
                    <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '0.9rem', lineHeight: 1 }}>{Math.round(daily.temperature_2m_max[idx])}°</Typography>
                    <Typography sx={{ ...monoSx, fontSize: '0.55rem', color: 'text.disabled' }}>{Math.round(daily.temperature_2m_min[idx])}°</Typography>
                    <Typography sx={{ ...monoSx, fontSize: '0.55rem', color: windColor, mt: '2px' }}>{wnd}mph</Typography>
                    {rain > 0 && <Typography sx={{ ...monoSx, fontSize: '0.55rem', color: '#dc2626' }}>💧{rain.toFixed(1)}"</Typography>}
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      ) : null}
    </Paper>
  );
}
